import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card } from '@safetyhub/ui';
import { COMPANY_TRUST_LEVEL_LABELS } from '@safetyhub/shared';
import { adminCompaniesApi, type AdminMergeBacklogItem } from '../lib/api';
import { Badge } from './Badge';

/**
 * 병합 보류(흡수 skip) 미해결 목록.
 *
 * 정본 클레임은 같은 사업자번호의 빈 껍데기만 흡수하고, **사람이나 업무 데이터가 매달린
 * 회사는 통째로 건너뛴다**(합치면 남의 신원이 섞이므로 승인 없이 할 수 없는 일이다).
 * 건너뛴 사실은 감사 로그에만 남아 아무도 보지 못했다 — 이 섹션이 그 큐다.
 *
 * 보류 건이 없으면 통째로 사라진다. 평소엔 비어 있는 게 정상이라, 빈 상태를 자리로
 * 남기면 사업장 목록 위에 죽은 공간이 생긴다.
 */
export default function MergeBacklogSection() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const limit = 10;

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'companies', 'merge-backlog', page],
    queryFn: () => adminCompaniesApi.mergeBacklog({ page, limit }),
    placeholderData: (prev) => prev,
  });

  const items = data?.data?.items ?? [];
  const total = data?.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  // 보류 건이 정리되어 페이지 수가 줄면 지금 페이지가 범위를 벗어난다 — 남은 마지막
  // 페이지로 당겨오지 않으면 빈 표만 남는다.
  useEffect(() => {
    if (data && page > totalPages) setPage(totalPages);
  }, [data, page, totalPages]);

  // 조회가 실패하면 조용히 사라지지 않는다 — 보류 건을 못 보는 것이 이 섹션이 막으려는
  // 바로 그 상태라, 빈 것과 못 읽은 것을 구분해 보여준다.
  if (error) {
    return (
      <Card radius="lg" className="mb-4">
        <p className="text-xs text-red-600">병합 보류 목록을 불러오지 못했습니다.</p>
      </Card>
    );
  }

  // 로딩 중이거나 보류가 없으면 자리를 차지하지 않는다
  if (isLoading || total === 0) return null;

  // 강조색(amber)은 Card 안쪽 박스가 소유한다 — Card 자신이 bg-white·border-line을 기본
  // 클래스로 들고 있어, className으로 같은 속성을 덧붙이면 Tailwind가 생성한 CSS 순서에
  // 따라 조용히 무시될 수 있다(Card docblock).
  return (
    <Card radius="lg" className="mb-4">
      <div className="rounded border border-amber-200 bg-amber-50 p-3 mb-3">
        <h3 className="text-sm font-semibold text-amber-900">
          병합 보류 {total.toLocaleString('ko-KR')}건
        </h3>
        <p className="mt-1 text-xs text-amber-800">
          정본 인증 시 같은 사업자번호의 파편을 흡수했지만, 아래 회사들은 사람·업무 데이터가
          매달려 있어 건너뛰었습니다. 합치면 서로 다른 사람이 하나로 묶일 수 있어 자동 처리
          대상이 아닙니다 — 확인 후 수동으로 정리해 주세요.
        </p>
      </div>

      <div className="overflow-x-auto rounded border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">사업자번호</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">정본</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">보류된 회사</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                매달린 실체 (현재)
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">발생 시각</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {items.map((item) => (
              <BacklogRow
                key={`${item.auditLogId}:${item.skipped.id}`}
                item={item}
                onOpen={(id) => navigate(`/companies/${id}`)}
              />
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-end gap-2 mt-3 text-xs">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="px-2 py-1 rounded bg-white border border-gray-300 text-gray-700 disabled:opacity-40"
          >
            이전
          </button>
          <span className="text-gray-600">
            {page} / {totalPages}
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="px-2 py-1 rounded bg-white border border-gray-300 text-gray-700 disabled:opacity-40"
          >
            다음
          </button>
        </div>
      )}
    </Card>
  );
}

function BacklogRow({
  item,
  onOpen,
}: {
  item: AdminMergeBacklogItem;
  onOpen: (companyId: string) => void;
}) {
  const { canonical, skipped } = item;
  const a = skipped.attachments;

  // 사유 축 — 감사 로그엔 없어 서버가 현재 상태에서 다시 센 값이다.
  // 그동안 사람이 빠졌으면 아무것도 안 보일 수 있으므로 그 경우도 말로 적는다.
  const reasons: string[] = [];
  if (a.members > 0) reasons.push(`소속 ${a.members}명`);
  if (a.siteContacts > 0) reasons.push(`현장 담당자 ${a.siteContacts}명`);
  if (a.inspections > 0) reasons.push(`점검 ${a.inspections}건`);
  if (a.reports > 0) reasons.push(`보고서 ${a.reports}건`);
  if (a.groups > 0) reasons.push(`그룹 ${a.groups}개`);
  if (a.hasSubscription) reasons.push('구독 보유');

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-3 py-2 text-xs font-mono text-gray-900 whitespace-nowrap">
        {item.registrationNumber ?? '-'}
      </td>
      <td className="px-3 py-2 text-xs text-gray-900">
        {canonical ? (
          <button
            type="button"
            onClick={() => onOpen(canonical.id)}
            className="text-left hover:underline"
          >
            <span className="font-medium">{canonical.name}</span>
            <span className="block text-gray-500">
              {COMPANY_TRUST_LEVEL_LABELS[canonical.trustLevel]}
            </span>
          </button>
        ) : (
          <span className="text-gray-400">-</span>
        )}
      </td>
      <td className="px-3 py-2 text-xs text-gray-900">
        <button
          type="button"
          onClick={() => onOpen(skipped.id)}
          className="text-left hover:underline"
        >
          <span className="font-medium">{skipped.name}</span>
          <span className="block text-gray-500">
            {COMPANY_TRUST_LEVEL_LABELS[skipped.trustLevel]}
            {skipped.createdByCompanyName ? ` · 등록: ${skipped.createdByCompanyName}` : ''}
          </span>
        </button>
      </td>
      <td className="px-3 py-2 text-xs text-gray-700">
        {reasons.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {reasons.map((reason) => (
              <Badge key={reason} color="orange" label={reason} />
            ))}
          </div>
        ) : (
          <span className="text-gray-500">현재는 매달린 실체 없음 (그 사이 정리된 것)</span>
        )}
      </td>
      <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">
        {new Date(item.occurredAt).toLocaleString('ko-KR')}
      </td>
    </tr>
  );
}
